/**
 * career-profile — **the Kompass**: the person's own document, mounted at `/compass`
 * (S567, ratified 2026-07-29). The `career-compass-hub` dashboard-card landing that
 * used to sit here is deleted and `/compass/profile` is a route-level redirect onto
 * this document. The »Profil« label is RETIRED — every state of this surface reads as
 * the Kompass.
 *
 * This is the thing the person associates the product's value with: a single seamless
 * document (§0 — NO cards) on ONE paper `.sheet` (§0.2 primitive, the DNA-Bible
 * idiom), gathering everything the pipeline elicited about them into one readable
 * page. It COMPOSES existing reads — the profile (identity / trajectory / facets /
 * constraints / voice + the LLM-authored Kandidatenprofil) and the Werte-Sektion
 * (»Mein Warum« + the top values + the three place-me statements) — and never
 * re-authors that material here (the values live in the W1 workshop track; this page
 * mirrors them and links back).
 *
 * ## The document's arc (§5 progression)
 *
 *   Warum → Werte → was dich antreibt (facets) → wohin du willst (trajectory) →
 *   auf einen Blick (positionierung / philosophie / vision / identität) →
 *   the ASSETS (Rahmenbedingungen, Stimme, …) → Kandidatenprofil → verwandt → weiter
 *
 * Every section renders ONLY when it holds something. An always-empty section
 * rendered as a gap would be the product lying about itself.
 *
 * ## The asset-section seam (P-A) — {@link KOMPASS_ASSET_SECTIONS}
 *
 * P-A: *the workshop elicits, the Kompass holds the asset.* A derived, value-bearing
 * asset (Rahmenbedingungen, Stimme, and — WS-D — the Schreibstil) is rendered here
 * CONSUMPTION-FIRST with edit as an affordance, never as a form. Adding one is a
 * REGISTRATION in `KOMPASS_ASSET_SECTIONS`, not a redesign: see the interface doc on
 * {@link KompassAssetSection} for the exact contract.
 *
 * R6: the Profile stream stays each asset's home and its SINGLE writer (constraints →
 * `profile.ConstraintsUpdated`). The Suchprofil workshop elicits/edits through the same
 * event, so the two surfaces are one truth — the asset's `originKey` copy says so out
 * loud, because a person who sees the same fact twice assumes there are two of them.
 *
 * Editing stays exactly where the person owns it: identity + every asset are
 * edit-in-place (the same revision procedures), and the narrative carries its
 * re-author + honest provenance / staleness / degraded cues. The Werte material,
 * the facets and the trajectory are read-only here (see the trajectory note below).
 *
 * The document also CONTRIBUTES its own section outline to the shell's left pane via
 * the {@link SectionNavBus}. The outline is ONLY in-page `#` anchors — the shell
 * splices them in as subordinate `kind: "anchor"` entries beneath the route the
 * person is on. A real app destination (the Suchprofil) is NOT an outline entry; it
 * lives in the document's »Verwandt« rail, where it belongs.
 */

import {
  KitElement,
  effect,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, classMap, type TemplateResult } from "@platform/web-kit/render"
import { seamAutoGrow } from "@platform/rlhf-elements"
import type { PersonalStatement, PersonalStatements, PersonalValueEntry, WritingStyleProfile } from "@career/definition-v2"
import { I18nToken, RouterToken, SectionNavToken, SessionToken, apiGet, apiPost } from "../services"
import {
  createProfileFacetDraftStore,
  draftList,
  draftText,
  serializeForm,
  type FacetFieldValue,
  type ProfileFacetDraftStore,
} from "../lib/profile-facet-draft"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { SectionNavBus, SectionNavItem, ShellSession } from "../domain"
import { authoredOnDate, narrativeBlocks } from "../lib/narrative"
import { nextStep } from "../lib/next-step"
import { writingStyleBlock } from "../lib/writing-style-block"
import { forwardDecl, toNextStep } from "../golden-path"

/** One narrative facet as the document reads it (`ProfileFacet` — statement + its Belege). */
interface FacetView {
  statement: string
  sources: number
}
/** Where the person is heading (`ProfileTrajectory`) — the document's forward-looking section. */
interface TrajectoryView {
  now: string
  midTerm: string
  longTerm: string
}
interface ProfileView {
  throughline: string
  selfDescription: string
  constraints: { location: string; salaryTarget: string; workModes: string[] }
  voiceSamples: string[]
  trajectory: TrajectoryView
  motivations: FacetView[]
  drives: FacetView[]
  antiValues: FacetView[]
  dealBreakers: FacetView[]
}
interface NarrativeView {
  body: string
  provenanceCount: number
  stale: boolean
  authoredOn: string | null
  /** M1: true when the persisted narrative is the deterministic composer fallback, not real prose. */
  degraded: boolean
}
/** The slice of the Werte-Sektion this asset mirrors (read-only): the top values + the four statements. */
interface SektionSlice {
  values: PersonalValueEntry[]
  statements: PersonalStatements | null
  produced: boolean
}

const WORK_MODES = ["remote", "hybrid", "onsite"] as const

/** The three »Auf einen Blick« statement kinds mirrored from the Werte-Sektion (»warum« is the hero). */
type StatementKind = PersonalStatement["kind"]
const GLANCE_ORDER: readonly Exclude<StatementKind, "warum">[] = ["positionierung", "philosophie", "vision"]
const GLANCE_LABEL: Record<Exclude<StatementKind, "warum">, CareerKey> = {
  positionierung: "profile.doc.positionierung",
  philosophie: "profile.doc.philosophie",
  vision: "profile.doc.vision",
}

/** The document sections, in render order — the SINGLE source the outline + the anchors share. */
const SECTION_ID = {
  warum: "kompass-warum",
  werte: "kompass-werte",
  antrieb: "kompass-antrieb",
  richtung: "kompass-richtung",
  positionierung: "kompass-positionierung",
  philosophie: "kompass-philosophie",
  vision: "kompass-vision",
  identitaet: "kompass-identitaet",
  kandidatenprofil: "kompass-kandidatenprofil",
} as const

/** An asset section's anchor id — derived from its registry key, never hand-written twice. */
const assetAnchor = (id: string): string => `kompass-${id}`

/** The route to the related Suchprofil asset (linked, never embedded). */
const SEARCH_PROFILE_ROUTE = "/workshops/search-profile"

/** The W4 workshop that elicits the Schreibstil asset — where a correction is actually made. */
const WRITING_STYLE_ROUTE = "/workshops/writing"

/**
 * The facet arrays the document renders, in reading order.
 *
 * `values` is DELIBERATELY ABSENT. The Werte-Sektion fan-out writes the *philosophie*
 * statement into `CandidateProfile.values` (`api-v2/src/workshop/werte-sektion.ts`
 * `FACET_FIELD_BY_STATEMENT`), and that same sentence already renders twice on this
 * page — once as »Deine Werte« (the `PersonalValueEntry` rows) and once as »Deine
 * Arbeitsphilosophie«. A third copy under a third heading would read as three
 * different facts. The field stays in the domain and in the narrative bundle; the
 * document just does not repeat itself.
 */
const FACET_FIELDS = ["motivations", "drives", "antiValues", "dealBreakers"] as const
type FacetField = (typeof FACET_FIELDS)[number]
const FACET_LABEL: Record<FacetField, CareerKey> = {
  motivations: "profile.facets.motivations",
  drives: "profile.facets.drives",
  antiValues: "profile.facets.antiValues",
  dealBreakers: "profile.facets.dealBreakers",
}

/** The trajectory horizons, in time order — `ProfileTrajectory`'s three fields. */
const TRAJECTORY_STAGES: readonly { readonly key: keyof TrajectoryView; readonly label: CareerKey }[] = [
  { key: "now", label: "profile.doc.richtung.now" },
  { key: "midTerm", label: "profile.doc.richtung.mid" },
  { key: "longTerm", label: "profile.doc.richtung.long" },
]

/** The top values, Lebenswert first then Superkräfte (Basiswerte are not the headline — mirrors W1). */
function topValues(values: PersonalValueEntry[]): PersonalValueEntry[] {
  const rank = (t: string): number => (t === "Lebenswert" ? 0 : 1)
  return values.filter((v) => v.tier !== "Basiswerte").sort((a, b) => rank(a.tier) - rank(b.tier))
}

export class ProfileVm implements ViewModel {
  readonly profile: Signal<ProfileView | null> = signal<ProfileView | null>(null)
  readonly narrative: Signal<NarrativeView | null> = signal<NarrativeView | null>(null)
  readonly sektion: Signal<SektionSlice | null> = signal<SektionSlice | null>(null)
  /** The W4 »Schreibstil« asset (P-A). Read here because the asset-section seam reads from
   *  the VM and never fetches on its own; null until the workshop is confirmed. */
  readonly writingStyle: Signal<WritingStyleProfile | null> = signal<WritingStyleProfile | null>(null)
  readonly authoring: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  /** Which section is being edited: "identitaet" | "rahmen" | "stimme" | null. */
  readonly editing: Signal<string | null> = signal<string | null>(null)
  readonly saving: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** The signed-in person's display name — the document's title when known. */
  readonly displayName: string | null
  /** The per-user profile URN (AX-4: the profile entity is DOM-addressable). */
  readonly urn: Urn
  /** Per-user backup of the open section's uncommitted revision — see `profile-facet-draft`. */
  private readonly facetDraft: ProfileFacetDraftStore

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    session: ShellSession,
    private readonly sectionNav: SectionNavBus,
  ) {
    this.t = i18n.t
    this.displayName = session.user?.name ?? null
    this.urn = `urn:career:profile:${session.user?.sub ?? "anon"}`
    this.facetDraft = createProfileFacetDraftStore(session.user?.sub ?? null)
    this.renderDeps = [this.profile, this.narrative, this.sektion, this.writingStyle, this.authoring, this.error, this.loaded, this.editing, this.saving, this.draftFields]
  }

  /**
   * The rescued field values for the open section, or null when nothing was rescued. Read by
   * every editor field through {@link facetText} / {@link facetList}, so the restored revision
   * wins over server truth — the whole point of the rescue.
   */
  readonly draftFields: Signal<Record<string, FacetFieldValue> | null> = signal<Record<string, FacetFieldValue> | null>(null)

  /** One editor field's value: the rescued revision if there is one, else the server's. */
  facetText(name: string, serverValue: string): string {
    return draftText(this.draftFields.value, name, serverValue)
  }

  /** One editor checkbox group's value: the rescued revision if there is one, else the server's. */
  facetList(name: string, serverValue: readonly string[]): readonly string[] {
    return draftList(this.draftFields.value, name, serverValue)
  }

  /**
   * Mirror the WHOLE open form on every keystroke/tick. Generic over the form rather than the
   * fields, so an asset registered later is protected without re-implementing anything.
   */
  mirrorFacet(section: string, form: HTMLFormElement): void {
    const fields = serializeForm(form)
    this.draftFields.set(fields)
    this.facetDraft.save({ section, fields, updatedAt: new Date().toISOString() })
  }

  /** Put a rescued revision back — reopen its section with the person's own text in it. */
  private restoreFacet(): void {
    const draft = this.facetDraft.load()
    if (!draft) return
    this.editing.set(draft.section)
    this.draftFields.set(draft.fields)
  }

  /** Drop the rescue copy — the revision is durable, or the person discarded it. */
  private clearFacetDraft(): void {
    this.draftFields.set(null)
    this.facetDraft.clear()
  }

  attach(): () => void {
    void this.refresh()
    // Contribute the document's section outline to the left pane (§7a); recomputes as the
    // document loads. Cleared on disconnect so other Kompass routes keep their default nav.
    const stopOutline = effect([this.profile, this.sektion, this.narrative, this.loaded], () =>
      this.sectionNav.set(this.outline()),
    )
    return () => {
      stopOutline()
      this.sectionNav.clear()
    }
  }

  private async refresh(): Promise<void> {
    // Compose two independent reads (mirrors career-werte-view): a missing Werte-Sektion still
    // lets the profile render, and vice-versa — one thin fetch never blanks the whole asset.
    const [p, sektion, writing] = await Promise.all([
      apiGet<{ profile: any; narrative: any; narrativeStale: boolean; narrativeAuthoredAt: string | null; narrativeDegraded?: boolean }>("/api/profile").catch(() => null),
      apiGet<SektionSlice>("/api/workshops/values/sektion").catch(() => null),
      apiGet<{ profile: WritingStyleProfile | null }>("/api/workshops/writing").catch(() => null),
    ])
    if (p) {
      this.profile.set(p.profile ? toProfileView(p.profile) : null)
      this.narrative.set(p.narrative ? toNarrativeView(p.narrative, p.narrativeStale, p.narrativeAuthoredAt, Boolean(p.narrativeDegraded)) : null)
    }
    this.sektion.set(sektion)
    this.writingStyle.set(writing?.profile ?? null)
    // AFTER the reads: an unsaved revision outranks the server truth it was edited from, and a
    // draft only ever exists while a revision is uncommitted (save/cancel clear it).
    this.restoreFacet()
    this.loaded.set(true)
  }

  // ── Presence: the SINGLE truth for which sections render + which anchors the outline offers ──

  private statementText(kind: StatementKind): string {
    return this.sektion.value?.statements?.statements.find((s) => s.kind === kind)?.text?.trim() ?? ""
  }
  get warumText(): string {
    return this.statementText("warum") || (this.profile.value?.throughline ?? "")
  }
  get topVals(): PersonalValueEntry[] {
    return topValues(this.sektion.value?.values ?? [])
  }
  get hasProfile(): boolean {
    return this.profile.value !== null
  }
  /** Anything to show at all? (Either the profile exists, or the Werte-Sektion was produced.) */
  get hasAnything(): boolean {
    return this.hasProfile || Boolean(this.sektion.value?.produced)
  }

  /** The facet groups that actually hold something — an empty group is never a heading. */
  get facetGroups(): { field: FacetField; facets: FacetView[] }[] {
    const p = this.profile.value
    if (!p) return []
    return FACET_FIELDS.map((field) => ({ field, facets: p[field] })).filter((g) => g.facets.length > 0)
  }
  /** The trajectory stages that carry text. Empty ⇒ the whole section stays invisible. */
  get trajectoryStages(): { label: CareerKey; text: string }[] {
    const t = this.profile.value?.trajectory
    if (!t) return []
    return TRAJECTORY_STAGES.map((s) => ({ label: s.label, text: t[s.key] })).filter((s) => s.text.length > 0)
  }
  /** The registered assets this person actually has something to read for (P-A seam). */
  get assetSections(): readonly KompassAssetSection[] {
    return KOMPASS_ASSET_SECTIONS.filter((a) => a.present(this))
  }

  /**
   * The left-pane document outline — ONLY in-page `#` anchors, and only for sections
   * that actually render. The shell splices these in as subordinate `kind: "anchor"`
   * entries beneath the route the person is on; a REAL app destination in here would
   * read as a route pretending to be a paragraph. (The Suchprofil is reachable from
   * the document's »Verwandt« rail.)
   */
  private outline(): SectionNavItem[] {
    if (!this.loaded.value || !this.hasAnything) return []
    const anchor = (id: string, label: CareerKey): SectionNavItem => ({ url: `#${id}`, match: `#${id}`, label: this.t(label) })
    const items: SectionNavItem[] = [anchor(SECTION_ID.warum, "profile.doc.warum")]
    if (this.topVals.length > 0) items.push(anchor(SECTION_ID.werte, "profile.doc.werte"))
    if (this.facetGroups.length > 0) items.push(anchor(SECTION_ID.antrieb, "profile.doc.antrieb"))
    if (this.trajectoryStages.length > 0) items.push(anchor(SECTION_ID.richtung, "profile.doc.richtung"))
    if (this.statementText("positionierung")) items.push(anchor(SECTION_ID.positionierung, "profile.doc.positionierung"))
    if (this.statementText("philosophie")) items.push(anchor(SECTION_ID.philosophie, "profile.doc.philosophie"))
    if (this.statementText("vision")) items.push(anchor(SECTION_ID.vision, "profile.doc.vision"))
    if (this.hasProfile) items.push(anchor(SECTION_ID.identitaet, "profile.doc.identitaet"))
    for (const asset of this.assetSections) items.push(anchor(assetAnchor(asset.id), asset.labelKey))
    if (this.hasProfile) items.push(anchor(SECTION_ID.kandidatenprofil, "profile.doc.kandidatenprofil"))
    return items
  }

  // ── Edit-in-place (identity / constraints / voice) — the same revision procedures ──

  edit(section: string): void {
    const next = this.editing.value === section ? null : section
    this.editing.set(next)
    // Opening (or closing) a section starts from server truth — nothing is typed yet, and a
    // rescue belonging to a DIFFERENT section must never bleed into this one's fields.
    this.clearFacetDraft()
  }
  cancelEdit(): void {
    this.editing.set(null)
    this.clearFacetDraft()
  }

  /** Optimistic-ish: POST the revision, then refresh from server truth (absolute end-state). */
  private async saveRevision(path: string, body: unknown): Promise<void> {
    this.saving.set(true)
    this.error.set(null)
    try {
      await apiPost(path, body)
      this.editing.set(null)
      // The revision is durable server-side now. Cleared BEFORE the refresh, so the re-read
      // cannot reopen the editor from a draft the save has just made redundant.
      this.clearFacetDraft()
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.saving.set(false)
    }
  }

  saveIdentity(throughline: string, selfDescription: string): Promise<void> {
    return this.saveRevision("/api/profile/identity", {
      identity: { throughline: throughline.trim() || null, selfDescription: selfDescription.trim() || null },
    })
  }
  saveConstraints(location: string, salaryTarget: string, workModes: string[]): Promise<void> {
    return this.saveRevision("/api/profile/constraints", {
      constraints: { location: location.trim() || null, salaryTarget: salaryTarget.trim() || null, workModes },
    })
  }
  saveVoice(samples: string[]): Promise<void> {
    return this.saveRevision("/api/profile/voice", { voiceProfile: { samples } })
  }

  async authorNarrative(): Promise<void> {
    this.authoring.set(true)
    this.error.set(null)
    try {
      // Read the POST's degraded flag so the cue shows IMMEDIATELY (not only after reload).
      const res = await apiPost<{ degraded?: boolean }>("/api/profile/narrative")
      await this.refresh()
      const n = this.narrative.value
      if (n && typeof res.degraded === "boolean") this.narrative.set({ ...n, degraded: res.degraded })
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.authoring.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

// ══════════════════════════════════════════════════════════════════════════════════════
// THE ASSET-SECTION SEAM (P-A / R6) — the ONE place a derived asset enters the Kompass.
// ══════════════════════════════════════════════════════════════════════════════════════

/**
 * One versioned, value-bearing asset rendered INTO the Kompass document.
 *
 * P-A: a workshop ELICITS (the raw information, the process); the Kompass HOLDS the
 * result as an asset whose purpose is CONSUMPTION — readable as a document passage,
 * editable in place, later shareable. R6 adds: the asset's stream is its single writer,
 * and the eliciting workshop edits through that same event. There is one truth.
 *
 * ## Adding an asset (this is a REGISTRATION, not a redesign)
 *
 * 1. Add a `KompassAssetSection` object and append it to {@link KOMPASS_ASSET_SECTIONS}.
 *    Position in the array = position in the document.
 * 2. `id` must be unique and URL-safe; the anchor is `kompass-${id}` and the edit key
 *    is `id` itself — the outline, the `<section id>` and `data-section` all derive from
 *    it, so nothing is written twice.
 * 3. Add the `labelKey` / `originKey` strings to BOTH the EN and DE i18n blocks under
 *    `profile.*`. `originKey` is not decoration: it is the sentence that stops the person
 *    believing there are two copies of the fact.
 * 4. If the asset's data is not already on `ProfileVm.profile`, add it to `ProfileView` +
 *    `toProfileView` (or add a signal + a read in `ProfileVm.refresh`) — the seam reads
 *    from the VM, it never fetches on its own.
 * 5. Read-only asset? Supply `read` only. Editable asset? Supply `fields` AND `save`;
 *    the document wraps them in the shared `<form>` + save/cancel chrome and dispatches
 *    `save-facet` to you. `save` must POST to the asset's SINGLE writer endpoint.
 *
 * WS-D's Schreibstil asset is the next registration: it supersedes/extends »Deine
 * Stimme« (`voiceProfile`), so it registers beside it — either as a sibling entry or by
 * replacing `stimmeAsset`'s `read`/`fields` once its own event carries the samples.
 */
export interface KompassAssetSection {
  /** Registry key: the anchor (`kompass-${id}`), the edit key, and the `data-section` value. */
  readonly id: string
  /** The asset's name, in the person's language — the document section heading. */
  readonly labelKey: CareerKey
  /** ONE line making the asset's origin + single truth legible. Never omit it. */
  readonly originKey: CareerKey
  /** The workshop route that elicits this asset, when there is one (rendered in the origin line). */
  readonly originRoute?: string
  /** Does this person have this asset at all? An always-absent section must never render. */
  present(vm: ProfileVm): boolean
  /** Consumption-first render — the asset AS a document passage, never a form. */
  read(vm: ProfileVm): TemplateResult
  /** The editor's FIELDS only; the document supplies the `<form>` and the save/cancel row. */
  fields?(vm: ProfileVm): TemplateResult
  /** Commit the form to the asset's single writer event. Required iff `fields` is supplied. */
  save?(vm: ProfileVm, form: HTMLFormElement): void
}

const fieldValue = (form: HTMLFormElement, name: string): string =>
  form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`)?.value ?? ""

/**
 * »Deine Rahmenbedingungen« — Ort / Gehaltsziel / Arbeitsmodi.
 *
 * R6 (ratified): the Profile stream stays this asset's home and single writer
 * (`profile.ConstraintsUpdated`); the Suchprofil workshop elicits and edits through the
 * SAME event. So the block stays on the document — framed as a rendered asset with edit
 * as an affordance, with the origin line saying out loud that the Suchprofil shows the
 * same fact rather than a second one.
 */
const rahmenAsset: KompassAssetSection = {
  id: "rahmen",
  labelKey: "profile.doc.rahmen",
  originKey: "profile.doc.rahmenOrigin",
  originRoute: SEARCH_PROFILE_ROUTE,
  present: (vm) => vm.hasProfile,
  read: (vm) => {
    const c = vm.profile.value?.constraints
    if (!c) return html`${nothing}`
    const modes = c.workModes.map((m) => vm.t(`profile.workmode.${m}` as CareerKey)).join(" · ")
    const empty = !c.location && !c.salaryTarget && c.workModes.length === 0
    if (empty) return html`<p class="ws-prose is-gap" lang="de">${vm.t("profile.doc.rahmenEmpty")}</p>`
    return html`<dl class="kb-facts">
      <dt lang="de">${vm.t("profile.constraints.location")}</dt><dd lang="de">${c.location || "—"}</dd>
      <dt lang="de">${vm.t("profile.constraints.salary")}</dt><dd lang="de">${c.salaryTarget || "—"}</dd>
      <dt lang="de">${vm.t("profile.constraints.workModes")}</dt><dd lang="de">${modes || "—"}</dd>
    </dl>`
  },
  fields: (vm) => {
    const c = vm.profile.value?.constraints ?? { location: "", salaryTarget: "", workModes: [] }
    return html`
      <label class="kb-field"><span class="kb-field-label" lang="de">${vm.t("profile.constraints.location")}</span>
        <input class="kb-input" name="location" type="text" .value=${vm.facetText("location", c.location)} lang="de" /></label>
      <label class="kb-field"><span class="kb-field-label" lang="de">${vm.t("profile.constraints.salary")}</span>
        <input class="kb-input" name="salaryTarget" type="text" .value=${vm.facetText("salaryTarget", c.salaryTarget)} lang="de" /></label>
      <fieldset class="kb-workmodes"><legend class="kb-field-label" lang="de">${vm.t("profile.constraints.workModes")}</legend>
        ${WORK_MODES.map(
          (m) => html`<label class="kb-workmode"><input type="checkbox" name="workMode" value=${m} ?checked=${vm.facetList("workMode", c.workModes).includes(m)} /><span lang="de">${vm.t(`profile.workmode.${m}` as CareerKey)}</span></label>`,
        )}
      </fieldset>`
  },
  save: (vm, form) => {
    const workModes = [...form.querySelectorAll<HTMLInputElement>('input[name="workMode"]:checked')].map((c) => c.value)
    void vm.saveConstraints(fieldValue(form, "location"), fieldValue(form, "salaryTarget"), workModes)
  },
}

/**
 * »Deine Stimme« — the writing samples that keep generated letters sounding like the
 * person. Single writer: `profile.VoiceUpdated`. WS-D's Schreibstil workshop derives a
 * richer asset from the same intent; this entry is where that supersession lands.
 */
const stimmeAsset: KompassAssetSection = {
  id: "stimme",
  labelKey: "profile.doc.stimme",
  originKey: "profile.doc.stimmeOrigin",
  // Operator ruling (2026-07-29): BOTH voice sections stay for this batch — the derived
  // Schreibstil does not hide the hand-typed samples. This entry therefore had to stop
  // claiming to be the voice applications are written in: whenever a Schreibstil exists it
  // OUTRANKS these samples (`resolveAuthorVoice`: asset > legacy > null), so `stimmeOrigin`
  // now names the precedence instead. Demoting this entry is recorded as polish backlog.
  present: (vm) => vm.hasProfile,
  read: (vm) => {
    const samples = vm.profile.value?.voiceSamples ?? []
    if (samples.length === 0) return html`<p class="ws-prose is-gap" lang="de">${vm.t("profile.voice.empty")}</p>`
    return html`<ul class="kb-voice" role="list">${samples.map((s) => html`<li lang="de">${s}</li>`)}</ul>`
  },
  fields: (vm) => html`
    <label class="kb-field"><span class="kb-field-label" lang="de">${vm.t("profile.voice.samples")}</span>
      <textarea class="rlhf-seam" name="samples" rows="1" ${seamAutoGrow()} lang="de" .value=${vm.facetText("samples", (vm.profile.value?.voiceSamples ?? []).join("\n"))}></textarea></label>`,
  save: (vm, form) => {
    const samples = fieldValue(form, "samples").split("\n").map((s) => s.trim()).filter((s) => s.length > 0)
    void vm.saveVoice(samples)
  },
}

/**
 * »Dein Schreibstil« — the DERIVED writing-voice asset (W4, P-A). Single writer:
 * `workshop.WritingStyleProduced` on the `writing_style` Workshop stream.
 *
 * Read-only here on purpose, and that is the interesting part: every other asset on this
 * page is edit-in-place, but this one is derived from three rounds of the person's own
 * writing and carries a per-trait `basis` naming which rounds support it. An in-place text
 * edit would produce a reading no round backs, and the traits beneath it would still cite
 * the rounds — the section would quietly start lying about its own provenance. Correcting
 * it means re-running the workshop, which is exactly where `originRoute` points.
 *
 * It renders BESIDE »Deine Stimme« rather than replacing it (operator ruling): the two have
 * different single writers, and collapsing them would create the duplicate truth R6 forbids.
 * When both exist this one WINS in generation, and both origin lines say so.
 */
const schreibstilAsset: KompassAssetSection = {
  id: "schreibstil",
  labelKey: "profile.doc.schreibstil",
  originKey: "profile.doc.schreibstilOrigin",
  originRoute: WRITING_STYLE_ROUTE,
  present: (vm) => vm.writingStyle.value !== null,
  read: (vm) => writingStyleBlock(vm.t, vm.writingStyle.value),
}

/** THE registry. Array order = document order. Append to add an asset; nothing else changes. */
export const KOMPASS_ASSET_SECTIONS: readonly KompassAssetSection[] = [rahmenAsset, schreibstilAsset, stimmeAsset]

const ICON_PENCIL = html`<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>`

export class CareerProfile extends KitElement<ProfileVm> {
  protected createViewModel(ctx: KitContext): ProfileVm {
    return new ProfileVm(ctx.inject(RouterToken), ctx.inject(I18nToken), ctx.inject(SessionToken), ctx.inject(SectionNavToken))
  }

  protected render(vm: ProfileVm): TemplateResult {
    const err = vm.error.value
    // Loading + build states read as the KOMPASS — the »Profil« label is retired (S567).
    if (!vm.loaded.value) {
      return html`<section class="surface profile" urn=${vm.urn} aria-labelledby="profile-title">
        <h1 id="profile-title" class="surface-title" lang="de">${vm.t("surface.kompass.title")}</h1>
      </section>`
    }
    if (!vm.hasAnything) return this.buildState(vm, err)

    return html`
      <section class="surface profile" urn=${vm.urn} aria-labelledby="profile-title">
        <div class="sheet ws-doc kb-doc">
          <header class="kb-doc-head">
            <p class="eyebrow" lang="de">${vm.t("profile.doc.kicker")}</p>
            <h1 id="profile-title" class="surface-title kb-doc-title" lang="de">${vm.displayName ?? vm.t("surface.kompass.title")}</h1>
            <p class="surface-lead" lang="de">${vm.t("profile.lead")}</p>
          </header>
          ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

          ${this.warumHero(vm)}
          ${this.werteBlock(vm)}
          ${this.facetsBlock(vm)}
          ${this.trajectoryBlock(vm)}
          ${this.glance(vm)}
          ${vm.assetSections.map((a) => this.assetSection(vm, a))}
          ${this.narrativeSection(vm)}
          ${this.related(vm)}
          ${this.goldenNext(vm)}
        </div>
      </section>
    `
  }

  /** The honest empty state — a person who has not built their Kompass yet (no broken doc). */
  private buildState(vm: ProfileVm, err: string | null): TemplateResult {
    return html`
      <section class="surface profile" urn=${vm.urn} aria-labelledby="profile-title">
        <div class="sheet ws-doc kb-doc">
          <header class="kb-doc-head">
            <p class="eyebrow" lang="de">${vm.t("profile.doc.kicker")}</p>
            <h1 id="profile-title" class="surface-title kb-doc-title" lang="de">${vm.t("surface.kompass.title")}</h1>
          </header>
          ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
          <div class="kb-build">
            <p class="ws-hero-quote" lang="de">${vm.t("profile.doc.buildTitle")}</p>
            <p class="surface-lead" lang="de">${vm.t("profile.doc.buildLead")}</p>
            <button class="primary" data-act="to-workshop" lang="de">${vm.t("werte.view.toWorkshop")}</button>
          </div>
        </div>
      </section>
    `
  }

  // ── Hero: »Dein Warum« as the serif pull-quote (left brand accent) ──

  private warumHero(vm: ProfileVm): TemplateResult {
    const warum = vm.warumText
    const isGap = warum.length === 0
    return html`<section class="ws-hero" id=${SECTION_ID.warum} aria-labelledby="kb-warum-label">
      <p class="ws-hero-label" id="kb-warum-label" lang="de">${vm.t("profile.doc.warum")}</p>
      <p class=${classMap({ "ws-hero-quote": true, "is-gap": isGap })} lang="de">${isGap ? vm.t("profile.doc.warumGap") : warum}</p>
    </section>`
  }

  // ── »Deine Werte«: scannable hairline rows (name + one-line essence) ──

  private werteBlock(vm: ProfileVm): TemplateResult | typeof nothing {
    const top = vm.topVals
    if (top.length === 0) return nothing
    return html`<section class="ws-section" id=${SECTION_ID.werte} aria-labelledby="kb-werte-label">
      <header class="ws-sec-head">
        <h2 class="ws-sec-label" id="kb-werte-label" lang="de">${vm.t("profile.doc.werte")}</h2>
        <span class="ws-sec-count" lang="de">${top.length} ${vm.t("sektion.doc.valuesUnit")}</span>
      </header>
      <div class="kb-vlist">${top.map((v) => this.valueRow(vm, v))}</div>
    </section>`
  }

  /** One read-only value row — a mark + name on the left, the one-line essence on the right. */
  private valueRow(vm: ProfileVm, entry: PersonalValueEntry): TemplateResult {
    const isLebenswert = entry.tier === "Lebenswert"
    const essence = (entry.leitsatz ?? entry.definition ?? "").trim()
    return html`<div class="kb-vrow">
      <span class=${classMap({ "kb-vmark": true, "kb-super": !isLebenswert })} aria-hidden="true"></span>
      <span class="kb-vname" lang="de">${entry.name}</span>
      ${essence.length > 0 ? html`<span class="kb-vessence" lang="de">${essence}</span>` : nothing}
    </div>`
  }

  // ── »Was dich antreibt«: the narrative facets the domain always held and nobody ever saw ──

  /**
   * Motive / Antriebe / was nicht passt / No-Gos — `CandidateProfile`'s `ProfileFacet[]`
   * arrays, written by `profile.FacetEnriched` (Werte-Sektion confirm + W1 interview) and
   * by the ingest extraction's `profile.Seeded`. Rendered ONLY where a group holds
   * something: these arrays are legitimately empty for a person who has not done W1, and
   * four empty headings would be the product describing work it never did.
   */
  private facetsBlock(vm: ProfileVm): TemplateResult | typeof nothing {
    const groups = vm.facetGroups
    if (groups.length === 0) return nothing
    return html`<section class="ws-section" id=${SECTION_ID.antrieb} aria-labelledby="kb-antrieb-label">
      <header class="ws-sec-head">
        <h2 class="ws-sec-label" id="kb-antrieb-label" lang="de">${vm.t("profile.doc.antrieb")}</h2>
      </header>
      <p class="kb-sec-hint" lang="de">${vm.t("profile.doc.antriebHint")}</p>
      <div class="ws-prose-list">
        ${groups.map(
          ({ field, facets }) => html`<div class="kb-glance-block">
            <p class="ws-block-label" lang="de">${vm.t(FACET_LABEL[field])}</p>
            <ul class="kb-facetlist" role="list">
              ${facets.map(
                (f) => html`<li lang="de">
                  <span class="kb-facet-text">${f.statement}</span>
                  ${f.sources > 0 ? html`<span class="kb-facet-src" lang="de">${vm.t("profile.facet.sources", { n: f.sources })}</span>` : nothing}
                </li>`,
              )}
            </ul>
          </div>`,
        )}
      </div>
    </section>`
  }

  // ── »Wohin du willst«: the document's forward-looking section (ProfileTrajectory) ──

  /**
   * now / mittelfristig / langfristig. The ONLY write path today is `profile.Seeded`
   * (the ingest extraction fills it from the person's own documents) — there is no
   * revise event, so this section is read-only on purpose rather than by omission.
   * Invisible when the trajectory is empty; see the patch note in the WS-B report.
   */
  private trajectoryBlock(vm: ProfileVm): TemplateResult | typeof nothing {
    const stages = vm.trajectoryStages
    if (stages.length === 0) return nothing
    return html`<section class="ws-section" id=${SECTION_ID.richtung} aria-labelledby="kb-richtung-label">
      <header class="ws-sec-head">
        <h2 class="ws-sec-label" id="kb-richtung-label" lang="de">${vm.t("profile.doc.richtung")}</h2>
      </header>
      <ol class="kb-arc" role="list">
        ${stages.map(
          (s) => html`<li class="kb-arc-step">
            <span class="kb-arc-mark" aria-hidden="true"></span>
            <span class="kb-arc-label" lang="de">${vm.t(s.label)}</span>
            <p class="kb-arc-text" lang="de">${s.text}</p>
          </li>`,
        )}
      </ol>
    </section>`
  }

  // ── »Auf einen Blick«: the place-me statements + the editable identity ──

  private glance(vm: ProfileVm): TemplateResult {
    const p = vm.profile.value
    return html`<section class="ws-section kb-glance" aria-labelledby="kb-glance-label">
      <header class="ws-sec-head">
        <h2 class="ws-sec-label" id="kb-glance-label" lang="de">${vm.t("sektion.doc.aufEinenBlick")}</h2>
      </header>
      <div class="ws-prose-list">
        ${GLANCE_ORDER.map((k) => this.statementProse(vm, k))}
        ${p ? this.identityBlock(vm, p) : nothing}
      </div>
    </section>`
  }

  // ── The asset sections (P-A seam) — consumption first, edit as an affordance ──

  /**
   * Renders ONE registered {@link KompassAssetSection}. Everything structural lives here
   * so a registration supplies only its own content: the heading, the origin line that
   * makes the single truth legible, the read passage, and — for an editable asset — the
   * shared `<form>` + save/cancel chrome around the spec's own fields.
   */
  private assetSection(vm: ProfileVm, asset: KompassAssetSection): TemplateResult {
    const editing = vm.editing.value === asset.id
    const editable = typeof asset.fields === "function"
    const labelId = `kb-asset-${asset.id}-label`
    return html`<section class=${classMap({ "ws-section": true, "kb-asset": true, "kb-editable": editable })} id=${assetAnchor(asset.id)} aria-labelledby=${labelId}>
      <header class="ws-sec-head">
        <h2 class="ws-sec-label" id=${labelId} lang="de">${vm.t(asset.labelKey)}</h2>
      </header>
      ${editing && asset.fields
        ? html`<form
            class="kb-edit-form"
            @input=${(e: Event) => vm.mirrorFacet(asset.id, e.currentTarget as HTMLFormElement)}
            @change=${(e: Event) => vm.mirrorFacet(asset.id, e.currentTarget as HTMLFormElement)}
          >
            ${asset.fields(vm)}
            ${this.saveCancel(vm, asset.id)}
          </form>`
        : html`
            ${asset.read(vm)}
            <p class="kb-sec-hint kb-asset-origin" lang="de">
              ${vm.t(asset.originKey)}
              ${asset.originRoute
                ? html` <a class="kb-asset-origin-link" href=${asset.originRoute} data-act="nav" data-url=${asset.originRoute} lang="de">${vm.t("profile.doc.suchprofil")}</a>`
                : nothing}
            </p>
            ${editable ? this.editAffordance(vm, asset.id) : nothing}`}
    </section>`
  }

  /** The Kandidatenprofil closes the document — the one AI-authored passage over everything above. */
  private narrativeSection(vm: ProfileVm): TemplateResult | typeof nothing {
    if (!vm.hasProfile && !vm.narrative.value) return nothing
    return html`<section class="ws-section" aria-labelledby="kb-kp-label">
      <header class="ws-sec-head">
        <h2 class="ws-sec-label" id="kb-kp-label" lang="de">${vm.t("profile.doc.kandidatenprofil")}</h2>
      </header>
      ${this.narrativeBlock(vm)}
    </section>`
  }

  /** A read-only place-me statement mirrored from the Werte-Sektion (rendered only when present). */
  private statementProse(vm: ProfileVm, kind: Exclude<StatementKind, "warum">): TemplateResult | typeof nothing {
    const text = vm.sektion.value?.statements?.statements.find((s) => s.kind === kind)?.text?.trim() ?? ""
    if (text.length === 0) return nothing
    const id = kind === "positionierung" ? SECTION_ID.positionierung : kind === "philosophie" ? SECTION_ID.philosophie : SECTION_ID.vision
    return html`<div class="kb-glance-block" id=${id}>
      <p class="ws-block-label" lang="de">${vm.t(GLANCE_LABEL[kind])}</p>
      <p class="ws-prose" lang="de">${text}</p>
    </div>`
  }

  private editAffordance(vm: ProfileVm, section: string): TemplateResult {
    return html`<div class="kb-afford">
      <button class="ws-iconbtn" data-act="edit-facet" data-section=${section} lang="de" aria-label=${vm.t("profile.edit")}>
        ${ICON_PENCIL}<span>${vm.t("profile.edit")}</span>
      </button>
    </div>`
  }

  private saveCancel(vm: ProfileVm, section: string): TemplateResult {
    return html`<div class="kb-edit-actions">
      <button class=${classMap({ "ws-btn-sm": true, "is-primary": true, "is-pending": vm.saving.value })} data-act="save-facet" data-section=${section} ?disabled=${vm.saving.value} lang="de">${vm.t("profile.edit.save")}</button>
      <button class="ws-btn-sm" data-act="cancel-facet" lang="de">${vm.t("profile.edit.cancel")}</button>
    </div>`
  }

  private identityBlock(vm: ProfileVm, p: ProfileView): TemplateResult {
    const editing = vm.editing.value === "identitaet"
    return html`<div class="kb-glance-block kb-editable" id=${SECTION_ID.identitaet}>
      <p class="ws-block-label" lang="de">${vm.t("profile.doc.identitaet")}</p>
      ${editing
        ? html`<form
            class="kb-edit-form"
            @input=${(e: Event) => vm.mirrorFacet("identitaet", e.currentTarget as HTMLFormElement)}
            @change=${(e: Event) => vm.mirrorFacet("identitaet", e.currentTarget as HTMLFormElement)}
          >
            <label class="kb-field"><span class="kb-field-label" lang="de">${vm.t("profile.identity.throughlineLabel")}</span>
              <input class="kb-input" name="throughline" type="text" .value=${vm.facetText("throughline", p.throughline)} lang="de" /></label>
            <label class="kb-field"><span class="kb-field-label" lang="de">${vm.t("profile.identity.selfDescriptionLabel")}</span>
              <textarea class="rlhf-seam" name="selfDescription" rows="1" ${seamAutoGrow()} lang="de" .value=${vm.facetText("selfDescription", p.selfDescription)}></textarea></label>
            ${this.saveCancel(vm, "identitaet")}
          </form>`
        : html`
            <p class="ws-prose" lang="de">${p.throughline || vm.t("review.profile.noThroughline")}</p>
            ${p.selfDescription ? html`<p class="kb-prose-2" lang="de">${p.selfDescription}</p>` : nothing}
            ${this.editAffordance(vm, "identitaet")}`}
    </div>`
  }

  /** The LLM-authored Kandidatenprofil narrative — the one AI-authored block, with its honest cues. */
  private narrativeBlock(vm: ProfileVm): TemplateResult {
    const n = vm.narrative.value
    return html`<div class=${classMap({ "kb-narrative": true, "is-degraded": Boolean(n?.degraded) })} id=${SECTION_ID.kandidatenprofil} aria-live="polite">
      ${n
        ? html`
            ${n.degraded ? html`<p class="narrative-degraded-cue" role="status" lang="de">${vm.t("profile.narrative.degraded")}</p>` : nothing}
            <div class="kb-narrative-body">
              ${narrativeBlocks(n.body).map((b) => (b.heading ? html`<h3 lang="de">${b.text}</h3>` : html`<p class="ws-prose" lang="de">${b.text}</p>`))}
            </div>
            <p class="kb-narrative-meta">
              <span class="chip" lang="de">${vm.t("review.narrative.provenance", { n: n.provenanceCount })}</span>
              ${n.stale
                ? html`<span class="chip is-gap" lang="de">${vm.t("review.narrative.stale")}</span>`
                : n.authoredOn
                  ? html`<span class="chip" lang="de">${vm.t("review.narrative.authoredOn", { date: n.authoredOn })}</span>`
                  : nothing}
            </p>
            <button data-act="author-narrative" class=${classMap({ "ws-btn-sm": true, "is-primary": Boolean(n.degraded), "is-pending": vm.authoring.value })} ?disabled=${vm.authoring.value} lang="de">
              ${vm.authoring.value ? vm.t("review.narrative.authoring") : vm.t("review.narrative.reauthor")}
            </button>`
        : html`
            <p class="ws-prose is-gap" lang="de">${vm.t("review.narrative.empty")}</p>
            <button data-act="author-narrative" class=${classMap({ "ws-btn-sm": true, "is-primary": true, "is-pending": vm.authoring.value })} ?disabled=${vm.authoring.value} lang="de">
              ${vm.authoring.value ? vm.t("review.narrative.authoring") : vm.t("review.narrative.author")}
            </button>`}
    </div>`
  }

  // ── The related Suchprofil asset (linked, never embedded — and NOT an outline entry) ──

  private related(vm: ProfileVm): TemplateResult {
    return html`<section class="ws-section kb-related" aria-labelledby="kb-related-label">
      <header class="ws-sec-head">
        <h2 class="ws-sec-label" id="kb-related-label" lang="de">${vm.t("profile.doc.related")}</h2>
      </header>
      <a class="kb-related-link" href=${SEARCH_PROFILE_ROUTE} data-act="nav" data-url=${SEARCH_PROFILE_ROUTE} lang="de">
        <span class="kb-related-title" lang="de">${vm.t("profile.doc.suchprofil")}</span>
        <span class="kb-related-hint" lang="de">${vm.t("profile.doc.suchprofilHint")}</span>
      </a>
    </section>`
  }

  /** The golden-path spine forward: from the Kompass onward into the search. */
  private goldenNext(vm: ProfileVm): TemplateResult | typeof nothing {
    const decl = forwardDecl("career-profile")
    if (!decl) return nothing
    return nextStep(vm.t("next.eyebrow"), toNextStep(decl, vm.t(decl.labelKey)))
  }

  protected override actions(vm: ProfileVm): ActionMap {
    const formOf = (event: Event): HTMLFormElement | null => (event.target as HTMLElement).closest("form")
    const val = (form: HTMLFormElement, name: string): string => form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`)?.value ?? ""
    return {
      "to-workshop": () => vm.go("/workshops/values"),
      "author-narrative": () => void vm.authorNarrative(),
      "edit-facet": ({ section }) => {
        if (section) vm.edit(section)
      },
      "cancel-facet": () => vm.cancelEdit(),
      // Identity is the document's own field; every ASSET routes through its registry
      // entry's `save` — a new asset never touches this dispatch.
      "save-facet": ({ section }, event) => {
        const form = formOf(event)
        if (!form || !section) return
        if (section === "identitaet") {
          void vm.saveIdentity(val(form, "throughline"), val(form, "selfDescription"))
          return
        }
        KOMPASS_ASSET_SECTIONS.find((a) => a.id === section)?.save?.(vm, form)
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}

/** One `ProfileFacet[]` → the document's view of it. Tolerant of wire drift (missing ⇒ empty). */
function toFacets(raw: unknown): FacetView[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((f: any): FacetView => ({
      statement: String(f?.statement ?? "").trim(),
      sources: Array.isArray(f?.claimRefs) ? f.claimRefs.length : 0,
    }))
    .filter((f) => f.statement.length > 0)
}

function toProfileView(p: any): ProfileView {
  return {
    throughline: String(p.identity?.throughline ?? ""),
    selfDescription: String(p.identity?.selfDescription ?? ""),
    constraints: {
      location: String(p.constraints?.location ?? ""),
      salaryTarget: String(p.constraints?.salaryTarget ?? ""),
      workModes: Array.isArray(p.constraints?.workModes) ? p.constraints.workModes.map(String) : [],
    },
    voiceSamples: Array.isArray(p.voiceProfile?.samples) ? p.voiceProfile.samples.map(String) : [],
    trajectory: {
      now: String(p.trajectory?.now ?? "").trim(),
      midTerm: String(p.trajectory?.midTerm ?? "").trim(),
      longTerm: String(p.trajectory?.longTerm ?? "").trim(),
    },
    motivations: toFacets(p.motivations),
    drives: toFacets(p.drives),
    antiValues: toFacets(p.antiValues),
    dealBreakers: toFacets(p.dealBreakers),
  }
}

function toNarrativeView(n: any, stale: boolean, authoredAt: string | null, degraded: boolean): NarrativeView {
  return {
    body: String(n.body ?? ""),
    provenanceCount: Array.isArray(n.provenance) ? n.provenance.length : 0,
    stale,
    authoredOn: authoredOnDate(authoredAt),
    degraded: degraded || Boolean(n.meta?.degraded),
  }
}
